import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useEffect,
} from "react";
import {
  User,
  GPTResponse,
  JobDescription,
  FeedbackUpdate,
  OrgQuestion,
  SessionQuestion,
  Question,
  type,
  track,
  FeedbackType,
} from "@/utils/types";
import { AppInsightsProvider } from "./components/AppInsightsProvider";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import Cookie from "universal-cookie";
import { useAuth0 } from "@auth0/auth0-react";
import {
  getTokenOrRefresh,
  getGPTResponseServer,
  getQuestionsDatabase,
  updateUserResumeQuestions,
  updateUserAnsweredQuestions,
  getOrgQuestions,
  getUser,
  updateUserVerification,
  updateUserSavedSessions,
  getGPTResponseAssessment,
} from "./api";
import {
  createQuestion,
  hasPremSub,
  idsToTag,
  isAdmin,
  noFeedback,
  specTypeToType,
} from "./utils/utils";
import { createSilentAudio } from "create-silent-audio";
import { useLocation, useNavigate } from "react-router-dom";
import { getPaymentInfo } from "./components/Pay";
import { formatString } from "./utils/formatString";
import { BITESIZEDQuestions } from "./utils/BiteSizedQuestions";
import { getInfo, removeInfo } from "./components/signup";

interface AppState {
  getq: number;
  orgQuestions: { [key: string]: OrgQuestion[] } | null;
  continueFunc: boolean;
  runSetQs: boolean;
  initialLoad: boolean;
  useWhiteboard: boolean;
  codingLang: string;
  start: boolean;
  qPrompt: GPTResponse[];
  loading: boolean;
  recognizedsp: string;
  interviewer: string;
  questions: { [key: string]: Question[] }; //{specType/tag:questions[]}
  currentQuestion: number;
  currScreen: number;
  role: string;
  domain: string;
  customQ: string;
  jobDescriptionQ: JobDescription;
  type: string;
  track: string;
  processing: boolean;
  showAns: boolean;
  showFeedback: boolean;
  showAction: boolean;
  canStop: boolean;
  next: boolean;
  previous: boolean;
  login: boolean;
  privateSession: boolean;
  showNextConfirmation: boolean;
  showHistoryModal: boolean;
  showsigninModal: boolean;
  showPaymentModal: boolean;
  showintroFormModal: boolean;
  showTranscript: boolean;
  showApiError: boolean;
  errorText: string;
  showSettings: boolean;
  showFeedbackBox: boolean;
  resume: { resumeText: string; resumeName: string };
  profileUser: User | null;
  userAudio: boolean;
  intQuestion: number;
  qType: string | null;
  questionQuery: string;
  speechLen: number;
  hitMaxLen: boolean;
  displayPastFeedback: boolean;
  pastInfo: any;
  retryPast: boolean;
  canPractice: boolean;
  beginningLoad: boolean;
  nextFunc: (() => void) | null;
  feedbackNextFunc: (() => void) | null;
  abortController: AbortController;
  memberFeedbackModal: ReactNode | null;
  questionModal: ReactNode | null;
  overlayComp: ReactNode | null;
  GPTCallCount: number;
  contextLength: number;
  promptArray: GPTResponse[];
  attempted: { [key: string]: boolean };
  difficulty: { [key: string]: string };
  showMic: boolean;
  audioEnabled: boolean;
  jobDescriptions: { [key: string]: string };
  session: SessionQuestion[];
  feedbackContext: FeedbackType;
  feedbackTranscript: GPTResponse[];
  authComplete: boolean;
  showVideo: boolean;
  isAssessment: boolean;
  endInterview: boolean;
  isPractice: boolean;
  practiceStep: number;
  egressId: string;
}

type Action =
  | { type: "UPDATE_getq"; payload: number }
  | {
      type: "UPDATE_orgQuestions";
      payload: { [key: string]: OrgQuestion[] } | null;
    }
  | { type: "UPDATE_useWhiteboard"; payload: boolean }
  | { type: "UPDATE_continueFunc"; payload: boolean }
  | { type: "UPDATE_runSetQs"; payload: boolean }
  | { type: "RESET_STATE" }
  | { type: "SET_STATE"; payload: AppState }
  | { type: "UPDATE_initialLoad"; payload: boolean }
  | { type: "UPDATE_start"; payload: boolean }
  | { type: "UPDATE_qPrompt"; payload: GPTResponse[] }
  | { type: "UPDATE_loading"; payload: boolean }
  | { type: "UPDATE_recognizedsp"; payload: string }
  | { type: "UPDATE_interviewer"; payload: string }
  | { type: "UPDATE_questions"; payload: { [key: string]: Question[] } }
  | { type: "UPDATE_memberFeedbackModal"; payload: ReactNode | null }
  | { type: "UPDATE_questionModal"; payload: ReactNode | null }
  | { type: "UPDATE_overlayComp"; payload: ReactNode | null }
  | { type: "UPDATE_currentQuestion"; payload: number }
  | { type: "UPDATE_currScreen"; payload: number }
  | { type: "UPDATE_role"; payload: string }
  | { type: "UPDATE_domain"; payload: string }
  | { type: "UPDATE_customQ"; payload: string }
  | { type: "UPDATE_jobDescriptionQ"; payload: JobDescription }
  | { type: "UPDATE_type"; payload: string }
  | { type: "UPDATE_track"; payload: string }
  | { type: "UPDATE_processing"; payload: boolean }
  | { type: "UPDATE_showAns"; payload: boolean }
  | { type: "UPDATE_showFeedback"; payload: boolean }
  | { type: "UPDATE_showAction"; payload: boolean }
  | { type: "UPDATE_canStop"; payload: boolean }
  | { type: "UPDATE_codingLang"; payload: string }
  | { type: "UPDATE_next"; payload: boolean }
  | { type: "UPDATE_previous"; payload: boolean }
  | { type: "UPDATE_login"; payload: boolean }
  | { type: "UPDATE_privateSession"; payload: boolean }
  | { type: "UPDATE_showNextConfirmation"; payload: boolean }
  | { type: "UPDATE_showHistoryModal"; payload: boolean }
  | { type: "UPDATE_showsigninModal"; payload: boolean }
  | { type: "UPDATE_showPaymentModal"; payload: boolean }
  | { type: "UPDATE_showintroFormModal"; payload: boolean }
  | { type: "UPDATE_showApiError"; payload: boolean }
  | { type: "UPDATE_showTranscript"; payload: boolean }
  | { type: "UPDATE_errorText"; payload: string }
  | { type: "UPDATE_showSettings"; payload: boolean }
  | { type: "UPDATE_showFeedbackBox"; payload: boolean }
  | {
      type: "UPDATE_resume";
      payload: { resumeText: string; resumeName: string };
    }
  | { type: "UPDATE_profileUser"; payload: User | null }
  | { type: "UPDATE_userAudio"; payload: boolean }
  | { type: "UPDATE_intQuestion"; payload: number }
  | { type: "UPDATE_qType"; payload: string | null }
  | { type: "UPDATE_questionQuery"; payload: string }
  | { type: "UPDATE_speechLen"; payload: number }
  | { type: "UPDATE_hitMaxLen"; payload: boolean }
  | { type: "UPDATE_displayPastFeedback"; payload: boolean }
  | { type: "UPDATE_pastInfo"; payload: any }
  | { type: "UPDATE_retryPast"; payload: boolean }
  | { type: "UPDATE_canPractice"; payload: boolean }
  | { type: "UPDATE_beginningLoad"; payload: boolean }
  | { type: "UPDATE_nextFunc"; payload: (() => void) | null }
  | { type: "UPDATE_feedbackNextFunc"; payload: (() => void) | null }
  | { type: "UPDATE_abortController"; payload: AbortController }
  | { type: "UPDATE_GPTCallCount"; payload: number }
  | { type: "UPDATE_currentSession"; payload: number }
  | { type: "UPDATE_contextLength"; payload: number }
  | { type: "PUSH_qPrompt"; payload: any }
  | { type: "PUSH_questions"; payload: Question }
  | { type: "UPDATE_sessions"; payload: {} }
  | { type: "SET_sessions"; payload: SessionQuestion[] }
  | { type: "SET_feedbackContext"; payload: FeedbackType }
  | { type: "SET_feedbacktranscript"; payload: GPTResponse[] }
  | { type: "UPDATE_authComplete"; payload: boolean }
  | { type: "SET_showVideo"; payload: boolean }
  | { type: "SET_isAssessment"; payload: boolean }
    | { type: "SET_endInterview"; payload: boolean }
  | { type: "SET_isPractice"; payload: boolean }
  | { type: "SET_practiceStep"; payload: number }
  | { type: "SET_egressId"; payload: string };

type AppContextType = {
  auth0: any;
  state: AppState;
  dispatch: React.Dispatch<Action>;
  reducers: any;
  navigateThenRun: (func: (() => void) | null, path: string) => void;
  showPastFeedback: (question: string, i: number | null) => void;
  goToStartCont: () => void;
  showError: (val: boolean) => void;
  getFeedback: () => void;
  showFeedbackPopUp: () => boolean;
  stopAudio: () => void;
  refreshUser: () => Promise<void>;
  pickQuestion: (endScreen?: boolean) => void;
  startFunc: (ind: number, resetsession?: boolean, token?:string) => void;
  getAnswer: () => void;
  getAction: () => void;
  isAuth: () => boolean;
  setQs: (
    abortControllerP: AbortController | null,
    q: Question | null
  ) => Promise<void>;
  getFeedbackAsync: () => Promise<void>;
  asyncGetAction: () => Promise<void>;
  asyncGetAnswer: () => Promise<void>;
  saveSession: () => void;
  resumeSession: (sesh: SessionQuestion[]) => void;
  gpt: (token?:string) => Promise<string | null>;
};

interface AppProviderProps {
  children: ReactNode;
}

const appInsights: ApplicationInsights = AppInsightsProvider();
const AppContext = createContext<AppContextType | undefined>(undefined);
const initialState: AppState = {
  getq: -1,
  orgQuestions: null,
  continueFunc: false,
  runSetQs: false,
  initialLoad: true,
  start: false,
  qPrompt: [],
  loading: true,
  recognizedsp: "",
  interviewer: "",
  questions: {},
  currentQuestion: -1,
  currScreen: 1,
  role: "",
  domain: "",
  customQ: "",
  jobDescriptionQ: { title: "", description: "" },
  type: "",
  track: "",
  processing: false,
  showAns: false,
  showFeedback: false,
  showAction: false,
  canStop: false,
  codingLang: "python",
  next: true,
  previous: false,
  login: false,
  privateSession: false,
  showNextConfirmation: false,
  showHistoryModal: false,
  showsigninModal: false,
  showPaymentModal: false,
  showintroFormModal: false,
  showTranscript: false,
  showApiError: false,
  errorText: "Encountered a problem reaching the api, retrying in 3 seconds...",
  showSettings: false,
  showFeedbackBox: false,
  resume: { resumeText: "", resumeName: "" },
  profileUser: null,
  userAudio: true,
  intQuestion: 1,
  qType: null,
  questionQuery: "",
  speechLen: 0,
  hitMaxLen: false,
  displayPastFeedback: false,
  pastInfo: {},
  retryPast: false,
  canPractice: false,
  beginningLoad: false,
  nextFunc: null,
  feedbackNextFunc: null,
  abortController: new AbortController(),
  memberFeedbackModal: null,
  questionModal: null,
  overlayComp: null,
  GPTCallCount: 0,
  contextLength: 500, //length of context to send to gpt
  promptArray: [],
  attempted: {},
  difficulty: {},
  showMic: true,
  audioEnabled: false,
  useWhiteboard: false,
  jobDescriptions: {},
  session: [],
  feedbackContext: [],
  feedbackTranscript: [],
  authComplete: false,
  showVideo: false,
  isAssessment: false,
  endInterview: false,
  isPractice: false,
  practiceStep: -1,
  egressId: "",
};

function appReducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case "UPDATE_getq":
      return { ...state, getq: action.payload };
    case "UPDATE_orgQuestions":
      return { ...state, orgQuestions: action.payload };
    case "UPDATE_useWhiteboard":
      return { ...state, useWhiteboard: action.payload };
    case "UPDATE_continueFunc":
      return { ...state, continueFunc: action.payload };
    case "UPDATE_runSetQs":
      return { ...state, runSetQs: action.payload };
    case "RESET_STATE":
      return initialState;
    case "SET_STATE":
      return { ...action.payload, abortController: new AbortController() };
    case "UPDATE_initialLoad":
      return { ...state, initialLoad: action.payload };
    case "UPDATE_start":
      return { ...state, start: action.payload };
    case "UPDATE_qPrompt":
      return { ...state, qPrompt: action.payload };
    case "UPDATE_loading":
      return { ...state, loading: action.payload };
    case "UPDATE_recognizedsp":
      return { ...state, recognizedsp: action.payload };
    case "UPDATE_interviewer":
      return { ...state, interviewer: action.payload };
    case "UPDATE_questions":
      return { ...state, questions: action.payload };
    case "UPDATE_memberFeedbackModal":
      return { ...state, memberFeedbackModal: action.payload };
    case "UPDATE_questionModal":
      return { ...state, questionModal: action.payload };
    case "UPDATE_overlayComp":
      return { ...state, overlayComp: action.payload };
    case "UPDATE_showTranscript":
      return { ...state, showTranscript: action.payload };
    case "UPDATE_currentQuestion":
      return { ...state, currentQuestion: action.payload };
    case "UPDATE_currScreen":
      return { ...state, currScreen: action.payload };
    case "UPDATE_role":
      return { ...state, role: action.payload };
    case "UPDATE_domain":
      return { ...state, domain: action.payload };
    case "UPDATE_customQ":
      return { ...state, customQ: action.payload };
    case "UPDATE_jobDescriptionQ":
      return { ...state, jobDescriptionQ: action.payload };
    case "UPDATE_type":
      return { ...state, type: action.payload };
    case "UPDATE_track":
      return { ...state, track: action.payload };
    case "UPDATE_processing":
      return { ...state, processing: action.payload };
    case "UPDATE_showAns":
      return { ...state, showAns: action.payload };
    case "UPDATE_showFeedback":
      return { ...state, showFeedback: action.payload };
    case "UPDATE_showAction":
      return { ...state, showAction: action.payload };
    case "UPDATE_canStop":
      return { ...state, canStop: action.payload };
    case "UPDATE_codingLang":
      return { ...state, codingLang: action.payload };
    case "UPDATE_next":
      return { ...state, next: action.payload };
    case "UPDATE_previous":
      return { ...state, previous: action.payload };
    case "UPDATE_login":
      return { ...state, login: action.payload };
    case "UPDATE_privateSession":
      return { ...state, privateSession: action.payload };
    case "UPDATE_showNextConfirmation":
      return { ...state, showNextConfirmation: action.payload };
    case "UPDATE_showHistoryModal":
      return { ...state, showHistoryModal: action.payload };
    case "UPDATE_showsigninModal":
      return { ...state, showsigninModal: action.payload };
    case "UPDATE_showPaymentModal":
      return { ...state, showPaymentModal: action.payload };
    case "UPDATE_showintroFormModal":
      return { ...state, showintroFormModal: action.payload };
    case "UPDATE_showApiError":
      return { ...state, showApiError: action.payload };
    case "UPDATE_errorText":
      return { ...state, errorText: action.payload };
    case "UPDATE_showSettings":
      return { ...state, showSettings: action.payload };
    case "UPDATE_showFeedbackBox":
      return { ...state, showFeedbackBox: action.payload };
    case "UPDATE_resume":
      return { ...state, resume: action.payload };
    case "UPDATE_profileUser":
      return { ...state, profileUser: action.payload };
    case "UPDATE_userAudio":
      return { ...state, userAudio: action.payload };
    case "UPDATE_intQuestion":
      return { ...state, intQuestion: action.payload };
    case "UPDATE_qType":
      return { ...state, qType: action.payload };
    case "UPDATE_questionQuery":
      return { ...state, questionQuery: action.payload };
    case "UPDATE_speechLen":
      return { ...state, speechLen: action.payload };
    case "UPDATE_hitMaxLen":
      return { ...state, hitMaxLen: action.payload };
    case "UPDATE_displayPastFeedback":
      return { ...state, displayPastFeedback: action.payload };
    case "UPDATE_pastInfo":
      return { ...state, pastInfo: action.payload };
    case "UPDATE_retryPast":
      return { ...state, retryPast: action.payload };
    case "UPDATE_canPractice":
      return { ...state, canPractice: action.payload };
    case "UPDATE_beginningLoad":
      return { ...state, beginningLoad: action.payload };
    case "UPDATE_nextFunc":
      return { ...state, nextFunc: action.payload };
    case "UPDATE_feedbackNextFunc":
      return { ...state, feedbackNextFunc: action.payload };
    case "UPDATE_abortController":
      return { ...state, abortController: action.payload };
    case "UPDATE_GPTCallCount":
      return { ...state, GPTCallCount: action.payload };
    case "UPDATE_contextLength":
      return { ...state, contextLength: action.payload };
    case "PUSH_qPrompt":
      return { ...state, qPrompt: [...state.qPrompt, action.payload] };
    case "PUSH_questions":
      const currentSet = state.questions[action.payload.specType];
      const newQs = { ...state.questions };
      newQs[action.payload.specType] = currentSet
        ? currentSet.concat(action.payload)
        : [action.payload];
      return { ...state, questions: newQs };
    case "UPDATE_sessions":
      const updatedSessions = state.session.map((session, ind) =>
        ind === state.currentQuestion
          ? { ...session, ...action.payload }
          : session
      );
      return { ...state, session: updatedSessions };
    case "SET_sessions":
      return { ...state, session: action.payload };
    case "SET_feedbackContext":
      return { ...state, feedbackContext: action.payload };
    case "SET_feedbacktranscript":
      return { ...state, feedbackTranscript: action.payload };
    case "UPDATE_authComplete":
      return { ...state, authComplete: action.payload };
    case "SET_showVideo":
      return { ...state, showVideo: action.payload };
    case "SET_isAssessment":
      return { ...state, isAssessment: action.payload };
     case "SET_endInterview":
      return { ...state, endInterview: action.payload };
    case "SET_isPractice":
      return { ...state, isPractice: action.payload };
    case "SET_practiceStep":
      return { ...state, practiceStep: action.payload };
    default:
      return state;
  }
}

export function AppProvider({ children }: AppProviderProps) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const location = useLocation();
  const navigate = useNavigate();

  const reducers = {
    setgetq: (getq: number) => dispatch({ type: "UPDATE_getq", payload: getq }),
    setorgQuestions: (orgQuestions: { [key: string]: OrgQuestion[] } | null) =>
      dispatch({ type: "UPDATE_orgQuestions", payload: orgQuestions }),
    setuseWhiteboard: (useWhiteboard: boolean) =>
      dispatch({ type: "UPDATE_useWhiteboard", payload: useWhiteboard }),
    setcontinueFunc: (continueFunc: boolean) =>
      dispatch({ type: "UPDATE_continueFunc", payload: continueFunc }),
    setrunSetQs: (runSetQs: boolean) =>
      dispatch({ type: "UPDATE_runSetQs", payload: runSetQs }),
    setWholeState: (newState: AppState) =>
      dispatch({ type: "SET_STATE", payload: newState }),
    setbeginningLoad: (beginningLoad: boolean) =>
      dispatch({ type: "UPDATE_beginningLoad", payload: beginningLoad }),
    setshowTranscript: (showTranscript: boolean) =>
      dispatch({ type: "UPDATE_showTranscript", payload: showTranscript }),
    setcanPractice: (canPractice: boolean) =>
      dispatch({ type: "UPDATE_canPractice", payload: canPractice }),
    setcanStop: (canStop: boolean) =>
      dispatch({ type: "UPDATE_canStop", payload: canStop }),
    setcodingLang: (codingLang: string) =>
      dispatch({ type: "UPDATE_codingLang", payload: codingLang }),
    setcontextLength: (contextLength: number) =>
      dispatch({ type: "UPDATE_contextLength", payload: contextLength }),
    setcurrScreen: (currScreen: number) =>
      dispatch({ type: "UPDATE_currScreen", payload: currScreen }),
    setcurrentQuestion: (currentQuestion: number) =>
      dispatch({ type: "UPDATE_currentQuestion", payload: currentQuestion }),
    setcustomQ: (customQ: string) =>
      dispatch({ type: "UPDATE_customQ", payload: customQ }),
    setdisplayPastFeedback: (displayPastFeedback: boolean) =>
      dispatch({
        type: "UPDATE_displayPastFeedback",
        payload: displayPastFeedback,
      }),
    setdomain: (domain: string) =>
      dispatch({ type: "UPDATE_domain", payload: domain }),
    setErrorText: (errorText: string) =>
      dispatch({ type: "UPDATE_errorText", payload: errorText }),
    setfeedbackNextFunc: (feedbackNextFunc: (() => void) | null) =>
      dispatch({ type: "UPDATE_feedbackNextFunc", payload: feedbackNextFunc }),
    setGPTCallCount: (GPTCallCount: number) =>
      dispatch({ type: "UPDATE_GPTCallCount", payload: GPTCallCount }),
    setcurrentSession: (currentSession: number) =>
      dispatch({ type: "UPDATE_currentSession", payload: currentSession }),
    sethitMaxLen: (hitMaxLen: boolean) =>
      dispatch({ type: "UPDATE_hitMaxLen", payload: hitMaxLen }),
    setinitialLoad: (initialLoad: boolean) =>
      dispatch({ type: "UPDATE_initialLoad", payload: initialLoad }),
    setintQuestion: (intQuestion: number) =>
      dispatch({ type: "UPDATE_intQuestion", payload: intQuestion }),
    setinterviewer: (interviewer: string) =>
      dispatch({ type: "UPDATE_interviewer", payload: interviewer }),
    setjobDescriptionQ: (jobDescriptionQ: JobDescription) =>
      dispatch({ type: "UPDATE_jobDescriptionQ", payload: jobDescriptionQ }),
    setloading: (loading: boolean) =>
      dispatch({ type: "UPDATE_loading", payload: loading }),
    setoverlayComp: (overlayComp: ReactNode | null) =>
      dispatch({
        type: "UPDATE_overlayComp",
        payload: overlayComp,
      }),
    setlogin: (login: boolean) =>
      dispatch({ type: "UPDATE_login", payload: login }),
    setmemberFeedbackModal: (memberFeedbackModal: ReactNode | null) =>
      dispatch({
        type: "UPDATE_memberFeedbackModal",
        payload: memberFeedbackModal,
      }),
    setnext: (next: boolean) =>
      dispatch({ type: "UPDATE_next", payload: next }),
    setNextFunc: (nextFunc: (() => void) | null) =>
      dispatch({ type: "UPDATE_nextFunc", payload: nextFunc }),
    setpastInfo: (pastInfo: any) =>
      dispatch({ type: "UPDATE_pastInfo", payload: pastInfo }),
    setprevious: (previous: boolean) =>
      dispatch({ type: "UPDATE_previous", payload: previous }),
    setprivateSession: (privateSession: boolean) =>
      dispatch({ type: "UPDATE_privateSession", payload: privateSession }),
    setprocessing: (processing: boolean) =>
      dispatch({ type: "UPDATE_processing", payload: processing }),
    setprofileUser: (profileUser: User | null) =>
      dispatch({ type: "UPDATE_profileUser", payload: profileUser }),
    setqPrompt: (qPrompt: GPTResponse[]) =>
      dispatch({ type: "UPDATE_qPrompt", payload: qPrompt }),
    setqType: (qType: string | null) =>
      dispatch({ type: "UPDATE_qType", payload: qType }),
    setquestionModal: (questionModal: ReactNode | null) =>
      dispatch({ type: "UPDATE_questionModal", payload: questionModal }),
    setquestionQuery: (questionQuery: string) =>
      dispatch({ type: "UPDATE_questionQuery", payload: questionQuery }),
    setquestions: (questions: { [key: string]: Question[] }) =>
      dispatch({ type: "UPDATE_questions", payload: questions }),
    setrecognizedsp: (recognizedsp: string) =>
      dispatch({ type: "UPDATE_recognizedsp", payload: recognizedsp }),
    setresume: (resume: { resumeText: string; resumeName: string }) =>
      dispatch({ type: "UPDATE_resume", payload: resume }),
    setretryPast: (retryPast: boolean) =>
      dispatch({ type: "UPDATE_retryPast", payload: retryPast }),
    setrole: (role: string) => dispatch({ type: "UPDATE_role", payload: role }),
    setshowAction: (showAction: boolean) =>
      dispatch({ type: "UPDATE_showAction", payload: showAction }),
    setshowAns: (showAns: boolean) =>
      dispatch({ type: "UPDATE_showAns", payload: showAns }),
    setshowApiError: (showApiError: boolean) =>
      dispatch({ type: "UPDATE_showApiError", payload: showApiError }),
    setshowFeedback: (showFeedback: boolean) =>
      dispatch({ type: "UPDATE_showFeedback", payload: showFeedback }),
    setshowFeedbackBox: (showFeedbackBox: boolean) =>
      dispatch({ type: "UPDATE_showFeedbackBox", payload: showFeedbackBox }),
    setshowHistoryModal: (showHistoryModal: boolean) =>
      dispatch({ type: "UPDATE_showHistoryModal", payload: showHistoryModal }),
    setshowintroFormModal: (showintroFormModal: boolean) =>
      dispatch({
        type: "UPDATE_showintroFormModal",
        payload: showintroFormModal,
      }),
    setshowNextConfirmation: (showNextConfirmation: boolean) =>
      dispatch({
        type: "UPDATE_showNextConfirmation",
        payload: showNextConfirmation,
      }),
    setshowPaymentModal: (showPaymentModal: boolean) =>
      dispatch({ type: "UPDATE_showPaymentModal", payload: showPaymentModal }),
    setshowSettings: (showSettings: boolean) =>
      dispatch({ type: "UPDATE_showSettings", payload: showSettings }),
    setshowsigninModal: (showsigninModal: boolean) =>
      dispatch({ type: "UPDATE_showsigninModal", payload: showsigninModal }),
    setspeechLen: (speechLen: number) =>
      dispatch({ type: "UPDATE_speechLen", payload: speechLen }),
    setstart: (start: boolean) =>
      dispatch({ type: "UPDATE_start", payload: start }),
    setAbortController: (abortController: AbortController) =>
      dispatch({ type: "UPDATE_abortController", payload: abortController }),
    settype: (type: string) => dispatch({ type: "UPDATE_type", payload: type }),
    settrack: (track: string) =>
      dispatch({ type: "UPDATE_track", payload: track }),
    setuserAudio: (userAudio: boolean) =>
      dispatch({ type: "UPDATE_userAudio", payload: userAudio }),
    pushqPrompt: (payload: any) => dispatch({ type: "PUSH_qPrompt", payload }),
    pushquestions: (payload: Question) =>
      dispatch({ type: "PUSH_questions", payload }),
    updatesession: (newVal: {}) =>
      dispatch({ type: "UPDATE_sessions", payload: newVal }),
    setsession: (session: SessionQuestion[]) =>
      dispatch({ type: "SET_sessions", payload: session }),
    setfeedbackcontext: (feedback: FeedbackType) =>
      dispatch({ type: "SET_feedbackContext", payload: feedback }),
    setfeedbacktranscript: (transcript: GPTResponse[]) =>
      dispatch({ type: "SET_feedbacktranscript", payload: transcript }),
    setauthComplete: (authComplete: boolean) =>
      dispatch({ type: "UPDATE_authComplete", payload: authComplete }),
    setshowVideo: (showVideo: boolean) =>
      dispatch({ type: "SET_showVideo", payload: showVideo }),
    setEgressId: (egressId: string) =>
      dispatch({ type: "SET_egressId", payload: egressId }),
    setisAssessment: (isAssessment: boolean) =>
      dispatch({ type: "SET_isAssessment", payload: isAssessment }),
    setendInterview: (endInterview: boolean) =>
      dispatch({ type: "SET_endInterview", payload: endInterview }),
    setisPractice: (isPractice: boolean) =>
      dispatch({ type: "SET_isPractice", payload: isPractice }),
    setpracticeStep: (practiceStep: number) =>
      dispatch({ type: "SET_practiceStep", payload: practiceStep }),
  };

  const {
    setstart,
    setqPrompt,
    setloading,
    setrecognizedsp,
    setinterviewer,
    setquestions,
    setcurrentQuestion,
    setcurrScreen,
    setrole,
    setdomain,
    setcustomQ,
    setjobDescriptionQ,
    settype,
    settrack,
    setprocessing,
    setshowAns,
    setshowFeedback,
    setshowAction,
    setcanStop,
    setnext,
    setinitialLoad,
    setprevious,
    setprivateSession,
    setshowNextConfirmation,
    setshowHistoryModal,
    setshowsigninModal,
    setshowintroFormModal,
    setshowPaymentModal,
    setshowTranscript,
    setshowApiError,
    setErrorText,
    setshowSettings,
    setshowFeedbackBox,
    setresume,
    setprofileUser,
    setuserAudio,
    setintQuestion,
    setqType,
    setquestionQuery,
    setspeechLen,
    sethitMaxLen,
    setdisplayPastFeedback,
    setpastInfo,
    setretryPast,
    setcanPractice,
    setbeginningLoad,
    setNextFunc,
    setfeedbackNextFunc,
    setAbortController,
    setGPTCallCount,
    setcontextLength,
    pushqPrompt,
    pushquestions,
    setgetq,
    setrunSetQs,
    setlogin,
    setcontinueFunc,
    setWholeState,
    setorgQuestions,
    updatesession,
    setsession,
    setauthComplete,
    setisAssessment,
    setisPractice,
    setendInterview,
    setshowVideo
  } = reducers;

  let audioElement: HTMLAudioElement | null = document.getElementById(
    "audioElement"
  ) as HTMLAudioElement | null;

  const supportedRoles = ["software engineering", "product management"];

  const typeDict: { [key: string]: string } = {
    "software engineeringbehavioral": "swehypothetical",
    "product managementbehavioral": "pmhypothetical",
  };

  const hasPremiumSub = (): boolean => {
    return hasPremSub(state.profileUser);
  };

  const saveSession = async (): Promise<void> => {
    const user2 = await updateUserSavedSessions(state.session);
    setprofileUser(user2);
    navigateThenRun(null, "/");
  };

  const resumeSession = (session: SessionQuestion[]): void => {
    let sq = session[0];
    if (sq) {
      setrole(sq.role);
      settrack(track.RESUMED);
      settype(sq.question.type);
      setcurrentQuestion(0);
      setqType("choose question");
      setrunSetQs(true);
      const qs: { [key: string]: Question[] } = {};
      session.forEach((x) => {
        qs[x.question.specType] = qs[x.question.specType] || [];
        qs[x.question.specType].push(x.question);
      });
      setquestions(qs);
      setsession(session);
    }
  };

  const asyncGetAnswer = async (): Promise<void> => {
    let promptComp = {
      specType: state.session[state.currentQuestion].question.specType,
      type: state.session[state.currentQuestion].question.type,
      role: state.role,
      codingLang: state.codingLang,
      question: state.session[state.currentQuestion].question.question,
      fullPrompt: state.session[state.currentQuestion].fullprompt,
      gptCallType: "solution",
    };

    const ans = await getGPTResponseServer(promptComp, showError);

    if (!state.abortController.signal.aborted) {
      updatesession({ samplesolution: extractAnswer(ans) });
      setloading(false);
    }
  };

  const goToStartCont = (): void => {
    state.abortController.abort();
    setstart(false);
    setqPrompt([]);
    setloading(true);
    setinterviewer("");
    setsession([]);
    setcurrScreen(1);
    setquestions({});
    setdomain("");
    setcustomQ("");
    setjobDescriptionQ({ description: "", title: "" });
    settype("");
    setprocessing(false);
    setretryPast(false);
    setshowAns(false);
    setshowAction(false);
    setshowFeedback(false);
    setcanStop(false);
    setnext(true);
    setprevious(false);
    setshowNextConfirmation(false);
    setshowHistoryModal(false);
    setshowsigninModal(false);
    setshowPaymentModal(false);
    setshowintroFormModal(false);
    setshowTranscript(false);
    setshowSettings(false);
    setshowFeedbackBox(false);
    setshowApiError(false);
    setErrorText(
      "Encountered a problem reaching the api, retrying in 3 seconds..."
    );
    setintQuestion(1);
    setfeedbackNextFunc(null);
    setqType(null);
    setquestionQuery("");
    setspeechLen(0);
    sethitMaxLen(false);
    setdisplayPastFeedback(false);
    setpastInfo({});
    setAbortController(new AbortController());
    setGPTCallCount(0);
    setcontextLength(500);
    setcurrentQuestion(-1);
    setgetq(-1);
    settrack("");
  };

  const showError = (val: boolean): void => {
    setshowApiError(val);
  };

  const asyncGetAction = async (): Promise<void> => {
    if (state.displayPastFeedback) {
      setshowAction(true);
    } else {
      let promptComp = {
        feedback: state.session[state.currentQuestion].feedback,
        gptCallType: "action",
      };
      const ans = await getGPTResponseServer(promptComp, showError);

      if (!state.abortController.signal.aborted) {
        updatesession({ actionsteps: ans });
        setloading(false);
      }
    }
  };

  const extractAnswer = (resp: string): string => {
    return resp;
  };

  const getAction = (): void => {
    stopAudio();
    if (state.session[state.currentQuestion].actionsteps === "") {
      setloading(true);
      asyncGetAction();
    }
    setshowAction(true);
    setshowAns(false);
    setshowFeedback(false);
  };

  const getAnswer = (): void => {
    stopAudio();

    if (state.session[state.currentQuestion].samplesolution === "") {
      setloading(true);
      asyncGetAnswer();
    }
    setshowAns(true);
    setshowAction(false);
    setshowFeedback(false);
  };

  const getFeedback = (): void => {
    if (state.canStop) {
      stopAudio();
    }
    if (state.session[state.currentQuestion].feedback === "") {
      setshowTranscript(false);
      setloading(true);
      getFeedbackAsync();
    }
    setshowAction(false);
    setshowAns(false);
    setshowFeedback(true);
    setrecognizedsp("");
  };

  const showPastFeedback = (
    question: string,
    i: number | null = null
  ): void => {
    if (state.profileUser && state.profileUser.answeredQuestions) {
      let q = state.profileUser.answeredQuestions[question];
      let ind = i === null ? q.length - 1 : i;
      let info = q[ind];
      let session: SessionQuestion[] = [
        {
          question: {
            question: question,
            level: null,
            domain: info["domain"] || "",
            type: "",
            specType: info["specType"] || "",
            subCategories: [],
          } as Question,
          role: info["role"],
          feedback: info["feedback"],
          actionsteps: info["actionSteps"] ? info["actionSteps"] : "",
          prompt: info["transcript"],
          fullprompt: [],
          samplesolution: info["samplesolution"] ? info["samplesolution"] : "",
          code: info["whiteboard"] ? info["whiteboard"] : "",
          useWhiteboard:
            info["whiteboard"] && info["whiteboard"] !== ""
              ? true
              : state.useWhiteboard,
          codingLang: info["codingLang"] ? info["codingLang"] : "",
          time: info["interviewDuration"] ? info["interviewDuration"] : -1,
          isHelpful: null,
          isHelpfulMessage: null,
          feedbackDuration: 0,
          sessionId: "",
        },
      ];
      setsession(session);
      setpastInfo({ ...info, index: ind });
      setshowFeedback(true);
      setshowHistoryModal(false);
      setdisplayPastFeedback(true);
      setretryPast(false);
      setloading(false);
      setcurrentQuestion(0);
    }
  };

  const getFeedbackAsync = async (): Promise<void> => {
    if (state.canStop) {
      stopAudio();
    }
    let prompt1 = [...state.session[state.currentQuestion].prompt];
    let fullprompt1 = [...state.session[state.currentQuestion].fullprompt];
    if (state.session[state.currentQuestion].code !== "") {
      const element = {
        role: "user",
        content: `Here is final ${
          state.codingLang
        } content I have on the whiteboard:\n \`\`\`${
          state.session[state.currentQuestion].code
        }\`\`\``,
      };
      prompt1.push(element);
      fullprompt1.push(element);
      updatesession({ prompt: prompt1 });
      updatesession({ fullprompt: fullprompt1 });
    }

    let feedbackL = "";
    if (state.session[state.currentQuestion].prompt.length === 0) {
      feedbackL = noFeedback;
    } else {
      let arrToUse =
        state.session[state.currentQuestion].question.specType !== "coding" &&
        state.session[state.currentQuestion].question.specType !==
          "system design" &&
        state.session[state.currentQuestion].question.specType !==
          "product design" &&
        state.session[state.currentQuestion].question.specType !== "estimation"
          ? prompt1
          : fullprompt1;
      const now = new Date();

      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      const day = now.getDate();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const seconds = now.getSeconds();

      let promptComp: { [key: string]: any } = {
        promptArr: arrToUse,
        specType: state.session[state.currentQuestion].question.specType,
        role: state.role,
        type: state.session[state.currentQuestion].question.type,
        domain: state.domain,
        question: state.session[state.currentQuestion].question.question,
        orgAccessible: !state.privateSession,
        gptCallType: "feedback",
        whiteboard:
          state.session[state.currentQuestion].code !== ""
            ? state.session[state.currentQuestion].code
            : null,
        codingLang:
          state.session[state.currentQuestion].code !== ""
            ? state.session[state.currentQuestion].codingLang
            : null,
        timestamp: `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`,
        transcript: prompt1.length === 0 ? [] : prompt1,
        sessionId: state.session[state.currentQuestion].sessionId,
        interviewDuration: state.session[state.currentQuestion].time,
      };
      if (
        state.track === "org" &&
        state.orgQuestions &&
        state.orgQuestions[state.role]
      ) {
        let rub = state.orgQuestions[state.role].find(
          (x) =>
            x.question == state.session[state.currentQuestion].question.question
        )?.rubric;
        if (rub !== null && rub !== "") {
          promptComp["rubric"] = rub;
        }
      }

      let t = await getGPTResponseServer(promptComp, showError);
      feedbackL = t?.message;
      if (t && t.user) {
        setprofileUser(t?.user);
      }
    }
    if (!state.abortController.signal.aborted) {
      setinterviewer(feedbackL);
      updatesession({ feedback: feedbackL });
      setshowFeedback(true);
    }
    setloading(false);
    setrecognizedsp("");
  };

  const GetQ = (newI: number): void => {
    if (newI >= 0) {
      setcurrentQuestion(newI);
      let q = state.session[newI].question.question.replace(/\\n/, "");
      setinterviewer(q);
      setloading(false);
    }
  };

  const showFeedbackPopUp = (): boolean => {
    return (
      state.currentQuestion >= 0 &&
      state.session.length > state.currentQuestion &&
      state.session[state.currentQuestion].feedback !== "" &&
      state.session[state.currentQuestion].feedback !== noFeedback &&
      !state.displayPastFeedback
    );
  };

  const gpt = async (token?:string): Promise<string | null> => {
    if (state.isPractice){
      let r = "great job! Now click Finish to continue on to the next question";
        let prompt1 = [...state.session[state.currentQuestion].prompt];
        prompt1.push({ role: "assistant", content: r })
        updatesession({ prompt: prompt1 });
        return r;
    }
    if (
      state.session[state.currentQuestion].question.specType !== "coding" &&
      state.session[state.currentQuestion].question.specType !==
        "system design" &&
      state.session[state.currentQuestion].question.specType !==
        "product design" &&
      state.session[state.currentQuestion].question.specType !== "estimation" &&
      !state.useWhiteboard
    ) {
      if (state.recognizedsp !== "") {
        setGPTCallCount(state.GPTCallCount + (state.isAssessment? 0 : 1));
        let prompt1 = [...state.session[state.currentQuestion].prompt];
        if (prompt1.length > 0 && prompt1[prompt1.length - 1].role == "user") {
          prompt1[prompt1.length - 1] = {
            role: "user",
            content: state.recognizedsp,
          };
        } else {
          prompt1.push({ role: "user", content: state.recognizedsp });
        }

        let response = "";
        if (state.isAssessment){
          const res = await getGPTResponseAssessment(state.recognizedsp, state.currentQuestion, state.session[state.currentQuestion].time,  token||"",  showError);
          response = res.response;
          setendInterview(res.end);
        }
        else{
          let obj = {
            transcript: prompt1,
            specType: state.session[state.currentQuestion].question.specType,
            type: state.session[state.currentQuestion].question.type,
            role: state.role,
            query: state.questionQuery,
            question: state.session[state.currentQuestion].question.question,
            gptCallType: "conversation",
          };
          response = await getGPTResponseServer(obj, showError);
        }
        if (!state.abortController.signal.aborted) {
          setinterviewer(response);
          prompt1.push({ role: "assistant", content: response });
        }
        updatesession({ prompt: prompt1 });
        setcontextLength(
          state.contextLength +
            response.split(" ").length +
            state.recognizedsp.split(" ").length
        );
        return response;
      }
    } else {
      let codeCont = `${
        state.session[state.currentQuestion].code !== ""
          ? `Firstly, here is what i have on the whiteboard:\n \`\`\`${
              state.session[state.currentQuestion].code
            }\`\`\``
          : ""
      }`;
      if (state.recognizedsp !== "" || codeCont !== "") {
        let fullprompt1 = [...state.session[state.currentQuestion].fullprompt];
        fullprompt1.push({
          role: "user",
          content: `${codeCont} ${state.recognizedsp}`,
        });
        setGPTCallCount(state.GPTCallCount + (state.isAssessment ? 0 : 1));
        let response = "";
        if (state.isAssessment){
          const res = await getGPTResponseAssessment(`${codeCont} ${state.recognizedsp}`, state.currentQuestion, state.session[state.currentQuestion].time,  token||"",  showError);
          response = res.response;
          setendInterview(res.end);
        }
        else{
          let obj = {
            transcript: fullprompt1,
            specType: state.session[state.currentQuestion].question.specType,
            type: state.session[state.currentQuestion].question.type,
            role: state.role,
            query: state.questionQuery,
            question: state.session[state.currentQuestion].question.question,
            gptCallType: "conversation",
          };
          response = await getGPTResponseServer(obj, showError);
        }
        if (!state.abortController.signal.aborted) {
          setinterviewer(response);
          let prompt1 = [...state.session[state.currentQuestion].prompt];
          if (
            prompt1.length > 0 &&
            prompt1[prompt1.length - 1].role == "user"
          ) {
            prompt1[prompt1.length - 1] = {
              role: "user",
              content: state.recognizedsp,
            };
          } else {
            prompt1.push({ role: "user", content: state.recognizedsp });
          }
          prompt1.push({ role: "assistant", content: response });
          updatesession({ prompt: prompt1 });
          fullprompt1.push({ role: "assistant", content: response });
          updatesession({ fullprompt: fullprompt1 });
        }
        setcontextLength(
          state.contextLength +
            response.split(" ").length +
            state.recognizedsp.split(" ").length +
            state.session[state.currentQuestion].code.split(" ").length
        );
        return response;
      }
    }
    return null;
  };

  const isOtherRole = (): boolean => {
    return !supportedRoles.includes(state.role);
  };

  const deriveQuestionQuery = (q: Question | null = null): string => {
    return `type=${q ? q.type : state.type}+role=${state.role}+spectype=${
      q ? q.specType : state.track
    }+specifics=${
      state.track === "domain"
        ? state.domain
        : state.track === "jobDescriptionQ"
        ? state.jobDescriptionQ.title
        : state.track === "custom"
        ? q == null
          ? state.customQ
          : q
        : ""
    }`;
  };

  const parseQs = (resp: string): string[] => {
    const result = resp.split(/\d+\)?\./);
    if (result[0] === "") {
      return result.splice(1);
    }
    return result;
  };

  const pickQuestion = (endScreen: boolean = false): void => {
    if (endScreen && showFeedbackPopUp()) {
      setfeedbackNextFunc(() => {
        navigateThenRun(() => {
          pickQuestionCont();
        }, "/track");
      });
    } else {
      navigateThenRun(() => {
        pickQuestionCont();
      }, "/track");
    }
  };

  const pickQuestionCont = (): void => {
    setintQuestion(5);
    setqType("choose question");
    setcurrScreen(1);
    setcurrentQuestion(-1);
    setgetq(-1);
    setstart(false);
    setloading(true);
    setprevious(false);
    resetState(null, true);
  };

  const refreshUser = async (): Promise<void> => {
    auth0.loginWithPopup();
  };

  const resetState = (
    newAbort: AbortController | null = null,
    noPopUp: boolean = false,
    resetsession: boolean = true
  ): void => {
    if (!noPopUp && showFeedbackPopUp()) {
      setfeedbackNextFunc(() => {
        resetStateCont(null, resetsession);
      });
    } else {
      resetStateCont(newAbort, resetsession);
    }
  };

  const resetStateCont = (
    newAbort: AbortController | null = null,
    resetsession: boolean
  ): void => {
    state.abortController.abort();
    setshowTranscript(false);
    if (resetsession) {
      updatesession({
        prompt: [],
        fullprompt: [],
        feedback: "",
        feedbackDuration: 0,
        time: 0,
        samplesolution: "",
        code: "",
        actionsteps: "",
        isHelpful: null,
        isHelpfulMessage: null,
        useWhiteboard: false,
      });
    }
    setprivateSession(false);
    setshowAns(false);
    setshowAction(false);
    setshowFeedback(false);
    setAbortController(newAbort ? newAbort : new AbortController());
    setspeechLen(0);
    setcontextLength(500);
    sethitMaxLen(false);
    setdisplayPastFeedback(false);
    setpastInfo({});
    setGPTCallCount(0);
    setcontinueFunc(false);
    setendInterview(false);
  };

  const SendUserFeedbackInfo = async (): Promise<void> => {
    //console.log(state.isHelpful);
    const qI: FeedbackUpdate = {
      isHelpful: state.session[state.currentQuestion].isHelpful,
      isHelpfulMessage: state.session[state.currentQuestion].isHelpfulMessage,
      actionSteps: state.session[state.currentQuestion].actionsteps,
      orgAccessible: !state.privateSession,
      samplesolution: state.session[state.currentQuestion].samplesolution,
    };
    const user2 = await updateUserAnsweredQuestions(
      state.session[state.currentQuestion].question.question,
      qI
    );
    setprofileUser(user2);
    setcontinueFunc(true);
  };

  const setQs = async (
    abortControllerP: AbortController | null = null,
    q: Question | null = null
  ): Promise<void> => {
    let abortControllerL = abortControllerP
      ? abortControllerP
      : state.abortController;
    let query = deriveQuestionQuery(q);
    const types = [];
    if (state.type === type.BOTH) {
      types.push(type.BEHAVIORAL, type.TECHNICAL);
    } else {
      types.push(state.type);
    }

    for (let i = 0; i < types.length; i++) {
      const intType = types[i];
      if (state.track === "custom" || q !== null) {
        setquestionQuery(query);
        if (q !== null) {
          setcustomQ(q.question);
        }
        setquestions({});
        pushquestions(
          q !== null
            ? q
            : createQuestion(state.customQ, null, "", "custom", "custom", [])
        );
        break;
        // } else if (state.type === "org" && state.orgQuestions) {
        //   setquestionQuery(query);
        //   setquestions(
        //     state.orgQuestions[state.role] === undefined
        //       ? []
        //       : state.orgQuestions[state.role].map((x) => x.question)
        //   );
      } else if (state.track === track.BITESIZED || q !== null) {
        setquestionQuery(query);
        setquestions({});
        pushquestions(
          q !== null
            ? q
            : createQuestion(
                formatString(
                  BITESIZEDQuestions[state.type].question,
                  state.role
                ),
                null,
                "",
                state.type,
                state.track,
                []
              )
        );
        break;
        // } else if (state.type === "org" && state.orgQuestions) {
        //   setquestionQuery(query);
        //   setquestions(
        //     state.orgQuestions[state.role] === undefined
        //       ? []
        //       : state.orgQuestions[state.role].map((x) => x.question)
        //   );
      } else if (
        (isOtherRole() &&
          state.track === "pack" &&
          intType !== type.ESSENTIAL) ||
        state.track === "domain"
      ) {
        if (state.questionQuery !== query) {
          if (i == 0) {
            setquestions({});
            setquestionQuery(query);
          }
          updateQuestions(intType, abortControllerL);
        }
      } else if (state.track === "resume based") {
        if (state.questionQuery !== query) {
          if (i == 0) {
            setquestions({});
            setquestionQuery(query);
          }
          updateQuestions(intType, abortControllerL);
        }
      } else if (state.track === "jobDescriptionQ") {
        if (state.questionQuery !== query) {
          if (i == 0) {
            setquestions({});
            setquestionQuery(query);
          }
          updateQuestions(intType, abortControllerL);
        }
      } else {
        // track = pack and regular role or type is essential
        if (state.questionQuery !== query) {
          if (i == 0) {
            setquestions({});
          }
          const resp = await getQuestionsDatabase(state.role, intType);
          for (const dbQs of resp) {
            if (!abortControllerL.signal.aborted) {
              const arr = dbQs.questions; // shuffleArray(resp);
              arr.forEach((x) => {
                let indPar = x.lastIndexOf("(");
                if (indPar !== -1) {
                  let q = x.slice(0, indPar - 1);
                  let level = x.slice(indPar + 1, -1);
                  pushquestions(
                    createQuestion(
                      q,
                      level,
                      state.domain,
                      specTypeToType[dbQs.id] || "",
                      idsToTag[dbQs.id] || dbQs.id,
                      []
                    )
                  );
                } else {
                  pushquestions(
                    createQuestion(
                      x,
                      null,
                      state.domain,
                      specTypeToType[dbQs.id] || "",
                      idsToTag[dbQs.id] || dbQs.id,
                      []
                    )
                  );
                }
              });
            }
          }
          setquestionQuery(query);
        }
      }
    }
  };

  const shuffleArray = (array: any[]): any[] => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  const isAuth = (): boolean => {
    return !auth0.isLoading && auth0.isAuthenticated;
  };

  // called when retry question
  const startFunc = (ind: number, resetsession: boolean = true, token: string = ""): void => {
    if (audioElement) {
      audioElement.src = createSilentAudio(10, 44100);
      audioElement.play();
    }
    if (showFeedbackPopUp()) {
      setfeedbackNextFunc(() => {
        navigateThenRun(() => {
          startFuncCont(ind, resetsession);
        }, state.isAssessment ? `/assessment?candidate=${token}` : "/interview");
      });
    } else {
      navigateThenRun(() => {
        startFuncCont(ind, resetsession);
      }, state.isAssessment ? `/assessment?candidate=${token}` : state.isPractice?  `/interview?candidate=${token}`: "/interview");
    }
  };

  const startFuncCont = (ind: number, resetsession: boolean): void => {
    if (!state.isAssessment && !hasPremiumSub()) {
      setshowPaymentModal(true);
    } else {
      setstart(true);
      let a = new AbortController();
      resetState(a, true, resetsession);
      setgetq(ind);
    }
  };

  const stopAudio = (): void => {
    if (audioElement) {
      audioElement.src = "";
      setcanStop(false); // in case bug on iPhone
    }
  };

  // this is called to potentially fetch questions from gpt
  const updateQuestions = async (
    intType: string = state.type,
    abortControllerP: AbortController | null = null
  ): Promise<void> => {
    //console.log("updateQuestions");
    let abortControllerL = abortControllerP
      ? abortControllerP
      : state.abortController;
    if (
      state.track === "resume based" &&
      state.profileUser &&
      state.profileUser.resumeQuestions &&
      state.profileUser.resumeQuestions.length !== 0
    ) {
      state.profileUser.resumeQuestions.forEach((x: string) =>
        pushquestions(
          createQuestion(x, null, "", "behavioral", "resume based", [])
        )
      );
      let res = "";
      state.profileUser.resumeQuestions.forEach(
        (x: string, ind: number) => (res += `${ind}. ${x}\n`)
      );
      pushqPrompt({ role: "assistant", content: res });
    } else {
      let obj = {
        current: state.qPrompt,
        role: state.role,
        type: intType,
        track: state.track,
        domain: state.domain,
        query: state.questionQuery,
        jobDescriptionQ: state.jobDescriptionQ.description,
        jobDescriptionQTitle: state.jobDescriptionQ.title,
        resumeText: state.resume.resumeText,
        gptCallType: "questions",
      };

      let resA: string[] = [];
      let resp = "";
      while (resA.length === 0) {
        resp = await getGPTResponseServer(obj, showError);
        if (!abortControllerL.signal.aborted) {
          let resD: string[];
          try {
            resD = JSON.parse(resp);
          } catch {
            resD = [resp];
          }
          resD.forEach((xp) => {
            const res = parseQs(xp);
            let a2: string[] = [];
            res.forEach((x: string) => {
              if (x !== "" && x !== "<br/>") a2.push(x.replace(/<br\/>/, ""));
            });
            const regex = /^[0-9]/;

            if (!regex.test(xp)) {
              a2.shift();
            }
            resA.push(...a2);
            pushqPrompt({ role: "assistant", content: xp });
          });
        }
      }
      if (!abortControllerL.signal.aborted) {
        resA.forEach((x: string) =>
          pushquestions(
            createQuestion(
              x,
              null,
              state.track === "domain" ? state.domain : "",
              intType,
              state.track === "resume based"
                ? "resume based"
                : state.track === "domain"
                ? state.domain
                : intType,
              []
            )
          )
        );
        if (state.track === "domain") {
          pushqPrompt({ role: "assistant", content: resp });
        }
        if (state.track === "resume based" && state.profileUser) {
          let user2 = await updateUserResumeQuestions(resA);
          setprofileUser(user2);
        }
      }
    }
  };

  const UpdateUserAQ = async (): Promise<void> => {
    //console.log(state.isHelpful);
    if (
      state.session[state.currentQuestion]?.feedback !== "" &&
      state.session[state.currentQuestion]?.feedback !== noFeedback
    ) {
      if (state.session[state.currentQuestion]?.isHelpful === null) {
        setshowFeedbackBox(true);
      } else {
        await SendUserFeedbackInfo();
      }
    } else {
      setcontinueFunc(true);
    }
  };

  const navigateThenRun = (func: (() => void) | null, path: string): void => {
    if (location.pathname !== path) {
      const {
        abortController,
        feedbackNextFunc,
        nextFunc,
        memberFeedbackModal,
        questionModal,
        overlayComp,
        ...state2
      } = state;
      window.history.replaceState({ key: location.key, state: state2 }, "");
    }
    if (func !== null) {
      func();
    }
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const auth0 = useAuth0();

  useEffect(() => {
    const fetchData = async () => {
      if(!auth0.isLoading && !auth0.isAuthenticated) {
        setauthComplete(true);
      }
      if (isAuth()) {
        setlogin(false);
        setshowsigninModal(false);
        let jwtToken = await auth0.getAccessTokenSilently();
        const cookie = new Cookie();
        cookie.set("oidToken", jwtToken, { path: "/", secure: true });
        //call our server
        let user = auth0.user;
        if (user) {
          let user1 = await getUser(
            user.email ?? "",
            user.name ?? "",
            user.email_verified ?? false
          );
          if (user1 !== null && appInsights) {
            appInsights.setAuthenticatedUserContext(user1.id);
            //re-enable verification
            if (user.email_verified && !user1.email_verified) {
              user1 = await updateUserVerification();
            }
            setprofileUser(user1);
            if (window.location.pathname === "/interview") {
              if (!hasPremSub(user1)) {
                setshowPaymentModal(true);
              }
            }
            if (window.location.pathname === "/interview") {
              if (!hasPremSub(user1)) {
                setshowPaymentModal(true);
              }
            }
            // if (user1 && isAdmin(user1) && Object.keys(user1.intakeForm).length > 0 && window.location.pathname !== "/nsbe") {
            //   navigateThenRun(null, "/admin");
            // }
          }
        }
        setauthComplete(true);
      }
    };
    fetchData();
  }, [auth0]); // Add dependencies if needed

  useEffect(() => {
    setresume(
      state.profileUser
        ? {
            resumeName: state.profileUser.resumename,
            resumeText: state.profileUser.resumetext,
          }
        : { resumeName: "", resumeText: "" }
    );
    setuserAudio(state.profileUser ? state.profileUser.audioEnabled : true);
  }, [state.profileUser]);

  useEffect(() => {
    if (state.beginningLoad && showFeedbackPopUp()) {
      setfeedbackNextFunc(() => {
        window.location.href = "https://www.thedevdifference.com";
      });
    }
  }, [state.beginningLoad]);

  useEffect(() => {
    const fetchToken = async () => {
      const tokenRes = await getTokenOrRefresh();
      if (tokenRes.authToken === null) {
        console.log(tokenRes.error);
      }
      if (audioElement) {
        audioElement.addEventListener("ended", () => {
          setcanStop(false);
        });
      }
    };
    fetchToken();
    document.addEventListener("click", (event: MouseEvent) => {
      const target = event?.target as Element;
      const dataElement = target?.getAttribute("data-custom-user-click");
      if (dataElement) {
        appInsights.trackEvent({
          name: "User Click",
          properties: { customId: dataElement },
        });
      }
    });
    return () => {
      document.removeEventListener("click", () => {});
    };
  }, []);

  useEffect(() => {
    const handleInitialLoad = async () => {
      if (state.initialLoad) {
        if (
          location.pathname !== "/" &&
          location.pathname !== "/signup" &&
          location.pathname !== "/pay" &&
          location.pathname !== "/admin" &&
          location.pathname !== "/profile" &&
          location.pathname !== "/preassessment"
        ) {
          window.location.href = "/";
        } else {
          setinitialLoad(false);
        }
      } else {
        if (
          window.history.state?.key === location.key &&
          window.history.state?.state !== undefined
        ) {
          setWholeState(window.history.state.state);
        }
      }
      if (location.pathname === "/interview") {
        if (!isAuth()) {
          setshowsigninModal(true);
        } else if (!state.canPractice) {
          setshowintroFormModal(true);
        } else if (!hasPremiumSub()) {
          setshowPaymentModal(true);
        }
      }
    };
    handleInitialLoad();
    appInsights.trackPageView({
      name: location.pathname,
      uri: location.pathname,
    });
  }, [location]);

  useEffect(() => {
    console.log(4);
    if (state.canPractice) {
      setshowintroFormModal(false);
      //getOrgQuestions().then((qs) => setorgQuestions(qs));
    }
  }, [state.canPractice]);

  useEffect(() => {
    if (state.showintroFormModal) {
      navigateThenRun(null, "/intake");
    } else if (state.profileUser) {
      //avoid it doing this unless profileuser i.e. intakeform was true at some point
      navigateThenRun(null, "/");
    }
  }, [state.showintroFormModal]);

  useEffect(() => {
    const updateUserAnsweredQuestions = async () => {
      if (state.feedbackNextFunc != null) {
        await UpdateUserAQ();
      }
    };
    updateUserAnsweredQuestions();
  }, [state.feedbackNextFunc]);

  useEffect(() => {
    if (state.login) {
      auth0.loginWithPopup();
    }
  }, [state.login]);

  // useEffect(() => {
  //   const scrollingDiv = document.getElementById("scrollingDiv");
  //   if (scrollingDiv !== null) {
  //     scrollingDiv.scrollTop = scrollingDiv.scrollHeight;
  //   }
  // }, [state.processing]);

  useEffect(() => {
    if (state.track !== "domain") {
      setdomain("");
    }
    if (state.track !== "jobDescriptionQ") {
      setjobDescriptionQ({ description: "", title: "" });
    }
  }, [state.track]);

  useEffect(() => {
    if (state.profileUser && !state.canPractice ) {
      if(location.pathname.includes("/preassessment"))
      {
        setcanPractice(true);
      }
      else if (
        !getInfo("nsbeprofile") &&
        (Object.keys(state.profileUser.intakeForm).length === 0 ||
          !state.profileUser.email_verified)
      ) {
        setshowintroFormModal(
          state.showPaymentModal !== true && !getPaymentInfo()
        );
      } else {
        if (getInfo("nsbeprofile")) {
          removeInfo("nsbeprofile");
        }
        setcanPractice(true);
      }
    }
  }, [state.profileUser]);

  useEffect(() => {
    if (state.retryPast === true) {
      navigateThenRun(() => {
        startFunc(0);
      }, "/interview");
    }
  }, [state.retryPast]);

  useEffect(() => {
    if (state.getq >= 0) {
      GetQ(state.getq);
    }
  }, [state.getq]);

  useEffect(() => {
    if (state.runSetQs) {
      setQs();
    }
    setrunSetQs(false);
  }, [state.runSetQs]);

  useEffect(() => {
    const sendUserFeedbackInfo = async () => {
      if (state.showFeedbackBox === false && state.feedbackNextFunc !== null) {
        await SendUserFeedbackInfo();
      }
    };
    sendUserFeedbackInfo();
  }, [state.showFeedbackBox]);

  useEffect(() => {
    const handleContinueFunc = async () => {
      if (state.continueFunc && state.feedbackNextFunc !== null) {
        state.feedbackNextFunc();
        setfeedbackNextFunc(null);
      }
    };
    handleContinueFunc();
  }, [state.continueFunc]);

  useEffect(() => {
    if (state.useWhiteboard === false) {
      updatesession({ code: "" });
    }
  }, [state.useWhiteboard]);

  return (
    <AppContext.Provider
      value={{
        auth0,
        state,
        dispatch,
        reducers,
        navigateThenRun,
        showPastFeedback,
        goToStartCont,
        showError,
        getFeedback,
        showFeedbackPopUp,
        stopAudio,
        refreshUser,
        pickQuestion,
        startFunc,
        getAnswer,
        getAction,
        isAuth,
        setQs,
        getFeedbackAsync,
        asyncGetAction,
        asyncGetAnswer,
        saveSession,
        resumeSession,
        gpt,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useAppContext() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
}
